import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Grid, FormControlLabel, Checkbox, Typography } from '@material-ui/core'
import { DeleteOutlined, DirectionsOutlined, HomeWorkOutlined, LocationCity } from '@material-ui/icons'
import { useSnackbar } from 'notistack'

import * as api from '../tools/API'
import { SiteInfoWebDetails } from '../tools/API'
import * as snacky from './CustomSnackbarProvider'
import * as navigator from '../tools/Navigator'
import { getErrorMessage, isDefined } from '../tools/Utils'
import { DetailsCard } from './DetailsCard'
import { TIButton } from './SmallComponents'
import { LabeledItem } from './LabeledItem'
import { ConfirmDialog } from './ConfirmDialog'

export function SiteInfoCard({siteInfoWebDetails, refreshSite}: {siteInfoWebDetails?: SiteInfoWebDetails, refreshSite?: () => Promise<void>}) {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const [description, setDescription] = useState('')
  const [isDeleting, setDeleting] = useState(false)
  const [inProgress, setInProgress] = useState(false)
  const [copyFiles, setCopyFiles] = useState(false)

  useEffect(() => { setDescription(siteInfoWebDetails?.description ?? '') }, [siteInfoWebDetails?.description])

  async function saveDescription(description: string) {
    try {
      await api.updateSiteInfoDescription(siteInfoWebDetails!.id, {description: description})
      siteInfoWebDetails!.description = description
      setDescription(description)
    } catch (err) {
      console.log(`Failed to save description: ${JSON.stringify(err)}`)
      enqueueSnackbar(t('errSave', {message: getErrorMessage(err)}), snacky.errorOpts)
    }
  }

  async function removeFromGroup() {
    setInProgress(true)
    try {
      await api.removeSiteInfoFromGroup(siteInfoWebDetails!.siteGroup!.id, siteInfoWebDetails!.id, copyFiles)
      enqueueSnackbar((t('siteGroupSiteRemovedInfo')), snacky.infoOpts)
      
      if (refreshSite) {
        await refreshSite()
      } else {
        siteInfoWebDetails!.siteGroup = undefined
      }
    } catch (err) {
      console.log(`Error removing site from group: ${JSON.stringify(err)}`)
      enqueueSnackbar(t('errRemoveSiteFromGroup', {message: getErrorMessage(err)}), snacky.errorOpts)
    } finally {
      setInProgress(false)
    }
  }

  const copyFilesOption = (
    <Box>
      <FormControlLabel
        control={
          <Checkbox
            checked={copyFiles}
            onChange={(e) => setCopyFiles(e.target.checked)}
            color="primary"
          />
        }
        label={
          <Typography variant="subtitle1" color="textSecondary">
            {t('siteInfoCopyFilesLabel')}
          </Typography>
        }
      />
      <Typography 
        variant="body2" 
        color="textSecondary" 
        style={{ marginLeft: 30, marginTop: 4 }}
      >
        {t('siteInfoCopyFilesHelp')}
      </Typography>
    </Box>
  );

  return (
    <DetailsCard cardId='siteInfo' title={t('siteInfoTitle')} icon={<LocationCity/>}>
      { siteInfoWebDetails &&
      <Grid container direction='column' spacing={1}>
        <LabeledItem
          label={t('siteInfoLabelSiteGroup')}
          value={siteInfoWebDetails.siteGroup?.name ?? '-'}
          action={
            isDefined(siteInfoWebDetails.siteGroup) && <Box display="flex" flexDirection="row">
              <TIButton
                onClick={() => navigator.push(`/site-group/${siteInfoWebDetails.siteGroup?.id}`)}
                target="_blank"
                rel="noopener"
                icon={<HomeWorkOutlined/>}
                tooltipKey="siteInfoSiteGroupTooltip"
              />
              <TIButton
                edge='end'
                disabled={inProgress}
                onClick={() => setDeleting(true)}
                target="_blank"
                rel="noopener"
                icon={<DeleteOutlined/>}
                tooltipKey="siteInfoSiteGroupRemoveTooltip"
              />
            </Box>
          }
        />
        <LabeledItem label={t('siteInfoLabelExtId')} value={siteInfoWebDetails.externalId?.toString() ?? 'N/A'} />
        <LabeledItem label={t('siteInfoLabelName')} value={siteInfoWebDetails.name} />
        <LabeledItem
          label={t('siteInfoLabelAddress')}
          value={formatAddress(siteInfoWebDetails)}
          action={
            <TIButton
              edge='end'
              href={getMapsLink(siteInfoWebDetails)}
              target="_blank"
              rel="noopener"
              icon={<DirectionsOutlined/>}
              tooltipKey="siteInfoLabelAddressTooltip"
            />
          }
        />
        <LabeledItem
          label={t('siteInfoLabelDescription')}
          value={description}
          onSave={description => saveDescription(description)}
          maxLength={3000}
        />
        { isDeleting && 
        <ConfirmDialog
          open={true}
          disabled={inProgress}
          title={t('siteInfoConfirmRemoveFromGroupTitle')}
          text={t('siteInfoConfirmRemoveFromGroupText', {name: siteInfoWebDetails.siteGroup?.name})}
          customContent={copyFilesOption}
          onAction={async (yes) => {
            if (yes) {
              await removeFromGroup()
            }
            setDeleting(false)
          }}
        />
        }
      </Grid>
      }
    </DetailsCard>
  )
}

function getMapsLink(site: SiteInfoWebDetails) {
  const address = formatAddress(site)
  return `https://www.google.com/maps/search/?api=1&query=${address}`
}

function formatAddress(site: SiteInfoWebDetails) {
  return `${site.streetAddress ? site.streetAddress+',' : ''} ${site.postCode ?? ''} ${site.city ?? ''}`
}
